/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';

// Types
import {
	TApiResponseProducerPopulated,
	TApiResponseGetProducersPopulated,
	TParsedProducer,
} from '../types/';
import {
	TApiParams,
	TParams,
} from '@global-types/app/app';

// Utils
import { formatDate } from '@utils/functions';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';
import { queryKeys } from '@config/constants/query-keys';
import { getCountryName } from '@utils/countries';
import isNil from 'lodash/isNil';
import { capitalize } from 'lodash';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getProducers = (
	params: TApiParams
): Promise<TApiResponseGetProducersPopulated> => {
	return apiClient.get('/producers', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			populate: [
				'organization',
				'contact',
				'verifiedBy',
				'logo',
			],
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

export const getProducersWithoutContact = (
	params: TApiParams
): Promise<TApiResponseGetProducersPopulated> => {
	return apiClient.get('/producers', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			populate: ['organization', 'verifiedBy', 'logo'],
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useProducersApi = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { checkRole } = useAuthorization();
	const isAdmin = checkRole([
		ROLES.ADMIN,
		ROLES.SUPERADMIN,
		ROLES.STAFF,
	]);

	const { data, isFetching, isFetched, isPending } =
		useQuery({
			queryKey: [
				queryKeys.app.producers.list.key,
				{
					page: page,
					size: size,
					queryParams: queryParams,
					sortParams: sortParams,
				},
			],
			queryFn: () => {
				if (isAdmin) {
					return getProducers({
						page: page,
						pageSize: size,
						queryParams: queryParams,
						sortParams: sortParams,
					});
				}
				return getProducersWithoutContact({
					page: page,
					pageSize: size,
					queryParams: queryParams,
					sortParams: sortParams,
				});
			},
			staleTime: 0,
		});

	const updateList = (
		Producers: TApiResponseProducerPopulated[]
	) =>
		Producers.reduce(
			(
				accumulator: TParsedProducer[],
				currentItem: TApiResponseProducerPopulated
			) => {
				const modifiedItem: TParsedProducer = {
					...currentItem,
					countryCode: currentItem?.location?.countryCode
						? getCountryName(
								currentItem.location.countryCode
							)
						: NOT_APPLICABLE,
					place: currentItem?.location?.place
						? currentItem.location.place
						: NOT_APPLICABLE,
					organization: currentItem?.organization?.name
						? currentItem.organization.name
						: NOT_APPLICABLE,
					organizationData: currentItem?.organization
						? currentItem.organization
						: NOT_APPLICABLE,
					createdAt: currentItem?.createdAt
						? formatDate(
								currentItem.createdAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					updatedAt: currentItem?.updatedAt
						? formatDate(
								currentItem.updatedAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					verifiedBy:
						!isNil(currentItem.verified) &&
						!isNil(currentItem.verifiedBy)
							? `${currentItem.verifiedBy.firstName} ${currentItem.verifiedBy.lastName}`
							: NOT_APPLICABLE,
					verifiedAt:
						!isNil(currentItem.verified) &&
						!isNil(currentItem.verifiedAt)
							? formatDate(
									currentItem.verifiedAt,
									'DD/MM/YYYY'
								)
							: NOT_APPLICABLE,
					legalIdentifier: currentItem?.legalIdentifier
						? currentItem.legalIdentifier
						: NOT_APPLICABLE,
					contactName: currentItem?.contact?.firstName
						? `${capitalize(currentItem.contact.firstName)} ${currentItem.contact.lastName}`
						: NOT_APPLICABLE,
					contactEmail: currentItem?.contact?.email
						? `${currentItem.contact.email}`
						: NOT_APPLICABLE,
					contactPhone: currentItem?.contact?.phoneNumber
						? `${currentItem.contact.phoneNumber}`
						: NOT_APPLICABLE,
				};
				accumulator.push(modifiedItem);

				return accumulator;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		isPending,
		total: data?.total ?? 0,
		page: data?.page ?? 0,
		pageSize: data?.pageSize ?? 0,
		totalPages: data?.totalPages ?? 0,
	};
};
